import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissoes } from '../hooks/usePermissoes';
import { Dropdown, Menu, Modal, Form, Input, message, Button, Drawer } from 'antd';
import { UserOutlined, KeyOutlined, LogoutOutlined, DownOutlined, MenuOutlined } from '@ant-design/icons';
import { platformIamService, usuarioService } from '../services/api';
import { MIN_PASSWORD_LENGTH, PASSWORD_MIN_LENGTH_MESSAGE, PASSWORD_PLACEHOLDER } from '../utils/passwordPolicy';
import { menuItemIdsByRoute, normalizeRole, routeResourceMap } from '../utils/rbacFrontend';
import { filterPlatformMenuItemsByRouteAccess } from '../utils/platformPermissions';
import '../styles/Sidebar.css';

const platformPermissionCache = new Map();
const platformPermissionInFlight = new Map();

const toPlatformPermissionSnapshot = (payload) => {
  const normalizedPermissions = Array.isArray(payload?.permissions)
    ? payload.permissions
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    : [];

  return {
    permissions: normalizedPermissions,
    usingLegacyFallback: payload?.using_legacy_fallback === true,
  };
};

const getPlatformPermissionCacheKey = (user) => {
  const fromId = String(user?.id || '').trim();
  if (fromId) {
    return `id:${fromId}`;
  }

  const fromPlatformUserId = String(user?.platform_user_id || '').trim();
  if (fromPlatformUserId) {
    return `platform:${fromPlatformUserId}`;
  }

  const fromEmail = String(user?.email || '').trim().toLowerCase();
  if (fromEmail) {
    return `email:${fromEmail}`;
  }

  return 'anonymous-platform-admin';
};

const getPlatformPermissionsCached = async (cacheKey) => {
  if (platformPermissionCache.has(cacheKey)) {
    return platformPermissionCache.get(cacheKey);
  }

  if (platformPermissionInFlight.has(cacheKey)) {
    return platformPermissionInFlight.get(cacheKey);
  }

  const request = platformIamService
    .getIamMePermissions()
    .then((response) => {
      const snapshot = toPlatformPermissionSnapshot(response?.data || {});
      platformPermissionCache.set(cacheKey, snapshot);
      return snapshot;
    })
    .finally(() => {
      platformPermissionInFlight.delete(cacheKey);
    });

  platformPermissionInFlight.set(cacheKey, request);
  return request;
};

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { podeVisualizar, isAdmin, loading: permissaoLoading, permissoes, error: permissaoError } = usePermissoes({
    enabled: user?.is_platform_admin !== true,
  });
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isSenhaModalVisible, setIsSenhaModalVisible] = useState(false);
  const [platformPermissionState, setPlatformPermissionState] = useState({
    loading: false,
    error: false,
    usingLegacyFallback: false,
    permissions: [],
  });
  const [emailForm] = Form.useForm();
  const [senhaForm] = Form.useForm();

  const resolvedUserName = (
    user?.nome_completo || user?.nome || user?.full_name || user?.email || 'Usuario'
  ).toString().trim();
  const resolvedUserEmail = (user?.email || '').toString().trim();
  const avatarInitial = resolvedUserName.charAt(0).toUpperCase() || 'U';
  const resolvedRole = (
    user?.role
    || user?.perfil?.nome
    || (typeof user?.perfil === 'string' ? user.perfil : '')
    || 'USUARIO'
  ).toString().trim();
  const roleBadgeClass = resolvedRole.toLowerCase();
  const roleKey = normalizeRole(resolvedRole);
  const isPlatformAdmin = user?.is_platform_admin === true;
  const hasOperationalTenant = Boolean(user?.current_tenant_id);
  const currentPath = String(location?.pathname || '').trim();
  const isPlatformSidebarTheme = isPlatformAdmin && currentPath.startsWith('/platform');

  React.useEffect(() => {
    if (!isPlatformAdmin) {
      setPlatformPermissionState({
        loading: false,
        error: false,
        usingLegacyFallback: false,
        permissions: [],
      });
      return;
    }

    const cacheKey = getPlatformPermissionCacheKey(user);
    let cancelled = false;

    setPlatformPermissionState((prev) => ({
      ...prev,
      loading: true,
      error: false,
    }));

    getPlatformPermissionsCached(cacheKey)
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        setPlatformPermissionState({
          loading: false,
          error: false,
          usingLegacyFallback: snapshot.usingLegacyFallback,
          permissions: snapshot.permissions,
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setPlatformPermissionState({
          loading: false,
          error: true,
          usingLegacyFallback: false,
          permissions: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, user]);

  if (!user) return null;

  const toggleSubmenu = (label) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const menuItensIds = Array.isArray(user?.menu_itens_ids)
    ? user.menu_itens_ids
    : Array.isArray(user?.perfil?.menu_itens_ids)
      ? user.perfil.menu_itens_ids
      : [];
  const hasMenuScope = menuItensIds.length > 0;
  const menuItensIdsSet = new Set(menuItensIds);

  const perfilTemMenuItem = (path) => {
    if (isAdmin) return true;
    const ids = menuItemIdsByRoute[path];
    if (!ids || ids.length === 0) return true;
    if (!hasOperationalTenant || !hasMenuScope) return false;
    return ids.some((id) => menuItensIdsSet.has(id));
  };

  // Definir itens de menu por role
  const getMenuItems = () => {
    // Subitem compartilhado -- usado tanto dentro do grupo "Academico" ja
    // existente para ADMIN/RH/OFFICE quanto no wrapper role-neutro abaixo
    // (para perfis sem grupo "Academico" proprio). Uma unica definicao evita
    // duplicidade de rota/recurso/icone entre os dois pontos de uso.
    const presencasSubmenuItem = { label: 'Presenças', path: '/presencas', icon: '📋' };
    // Roles com grupo "Academico" proprio ja recebem presencasSubmenuItem
    // dentro dele (ver itemsByRole abaixo); os demais perfis usam o wrapper
    // role-neutro em commonItems para nao perder o item quando tiverem grant.
    const rolesComAcademicoProprio = new Set(['ADMIN', 'RH', 'OFFICE']);

    const baseItems = [
      { 
        label: 'Dashboards', 
        icon: '📊',
        submenu: [
          { label: 'Executivo', path: '/dashboard', icon: '💼' },
          { label: 'Financeiro', path: '/dashboard-financeiro', icon: '💰' },
          { label: 'Parcelas', path: '/dashboard-parcelas', icon: '🧾' },
          { label: 'Operacional', path: '/dashboard-operacional', icon: '📚' },
          { label: 'Comercial', path: '/dashboard-comercial', icon: '🎯' },
          { label: 'RH', path: '/dashboard-rh', icon: '👥' },
        ]
      },
    ];

    const itemsByRole = {
      ADMIN: [
        { 
          label: 'Dashboards', 
          icon: '📊',
          submenu: [
            { label: 'Executivo', path: '/dashboard', icon: '💼' },
            { label: 'Executivo V2', path: '/dashboard-executivo-v2', icon: '💼' },
            { label: 'Financeiro', path: '/dashboard-financeiro', icon: '💰' },
            { label: 'Parcelas', path: '/dashboard-parcelas', icon: '🧾' },
            { label: 'Operacional', path: '/dashboard-operacional', icon: '📚' },
            { label: 'Comercial', path: '/dashboard-comercial', icon: '🎯' },
            { label: 'RH', path: '/dashboard-rh', icon: '👥' },
            { label: 'Unidades', path: '/dashboard-unidades', icon: '🏢' },
          ]
        },
        { 
          label: 'Sistema', 
          icon: '⚙️',
          submenu: [
            { label: 'Usuários', path: '/usuarios', icon: '👥' },
            { label: 'Perfis', path: '/perfis', icon: '🔐' },
          ]
        },
        {
          label: 'Configurações',
          icon: '🛠️',
          submenu: [
            { label: 'Financeiro', path: '/configuracoes/financeiro', icon: '🏦' },
            { label: 'Empresa', path: '/configuracoes/empresa', icon: '🏢' },
            { label: 'Minha Assinatura', path: '/conta/assinatura', icon: '🧾' },
          ]
        },
        { 
          label: 'Unidades', 
          icon: '🏢',
          submenu: [
            { label: 'Unidades', path: '/unidades', icon: '🏢' },
            { label: 'Tipos de Unidades', path: '/tipo-unidades', icon: '📋' },
            { label: 'Tipos de Contrato', path: '/tipo-contratos', icon: '📑' },
            { label: 'Séries', path: '/series', icon: '🧩' },
            { label: 'Modalidades', path: '/modalidades', icon: '🎯' },
          ]
        },
        {
          label: 'Equipe',
          icon: '👨‍💼',
          submenu: [
            { label: 'Equipe', path: '/equipe', icon: '👨‍💼' },
            { label: 'Aulas Dadas', path: '/aulas-dadas', icon: '✅' },
            { label: 'Relatório de Aulas', path: '/relatorio-mensal', icon: '📊' },
          ]
        },
        {
          label: 'Cadastros',
          icon: '🧾',
          submenu: [
            { label: 'Alunos', path: '/alunos', icon: '👨‍🎓' },
            { label: 'Responsáveis', path: '/responsaveis/pendentes', icon: '👤' },
          ]
        },
        {
          label: 'Acadêmico',
          icon: '🎓',
          submenu: [
            { label: 'Turmas', path: '/turmas', icon: '📚' },
            { label: 'Matrículas', path: '/matriculas', icon: '📝' },
            { label: 'Relatórios Operacionais', path: '/relatorios-operacionais', icon: '📊' },
            presencasSubmenuItem,
          ]
        },
        {
          label: 'Financeiro',
          icon: '💵',
          submenu: [
            { label: 'Fluxo de Caixa', path: '/fluxo-caixa', icon: '📈' },
            { label: 'Receitas', path: '/receitas', icon: '💰' },
            { label: 'Parcelas', path: '/parcelas', icon: '🧾' },
            { label: 'Faturas - Operacional', path: '/faturas/operacional', icon: '⚙️' },
            { label: 'Faturas - Gestão', path: '/faturas/gestao', icon: '🗂️' },
            { label: 'Despesas', path: '/despesas', icon: '💸' },
            { label: 'Pagamentos', path: '/folha-pagamento', icon: '💵' },
            { label: 'Fornecedores', path: '/fornecedores', icon: '🏪' },
            { label: 'Categorias Despesa', path: '/categorias-despesa', icon: '🏷️' },
            { label: 'Relatórios Contábeis', path: '/relatorios-contabeis', icon: '📑' },
          ]
        },
      ],
      RH: [
        { label: 'Dashboard RH', path: '/dashboard-rh', icon: '📊' },
        { label: 'Dashboard Parcelas', path: '/dashboard-parcelas', icon: '🧾' },
        {
          label: 'Cadastros',
          icon: '🧾',
          submenu: [
            { label: 'Alunos', path: '/alunos', icon: '👨‍🎓' },
            { label: 'Responsáveis', path: '/responsaveis/pendentes', icon: '👤' },
          ]
        },
        {
          label: 'Acadêmico',
          icon: '🎓',
          submenu: [
            { label: 'Turmas', path: '/turmas', icon: '📚' },
            { label: 'Matrículas', path: '/matriculas', icon: '📝' },
            { label: 'Relatórios Operacionais', path: '/relatorios-operacionais', icon: '📊' },
            presencasSubmenuItem,
          ]
        },
        {
          label: 'Equipe',
          icon: '👨‍💼',
          submenu: [
            { label: 'Equipe', path: '/equipe', icon: '👨‍💼' },
            { label: 'Aulas Dadas', path: '/aulas-dadas', icon: '✅' },
            { label: 'Relatório de Aulas', path: '/relatorio-mensal', icon: '📊' },
          ]
        },
        {
          label: 'Financeiro',
          icon: '💵',
          submenu: [
            { label: 'Fluxo de Caixa', path: '/fluxo-caixa', icon: '📈' },
            { label: 'Receitas', path: '/receitas', icon: '💰' },
            { label: 'Parcelas', path: '/parcelas', icon: '🧾' },
            { label: 'Faturas - Operacional', path: '/faturas/operacional', icon: '⚙️' },
            { label: 'Faturas - Gestão', path: '/faturas/gestao', icon: '🗂️' },
            { label: 'Despesas', path: '/despesas', icon: '💸' },
            { label: 'Pagamentos', path: '/folha-pagamento', icon: '💵' },
            { label: 'Fornecedores', path: '/fornecedores', icon: '🏪' },
            { label: 'Categorias Despesa', path: '/categorias-despesa', icon: '🏷️' },
            { label: 'Relatórios Contábeis', path: '/relatorios-contabeis', icon: '📑' },
          ]
        },
      ],
      OFFICE: [
        { label: 'Dashboard Operacional', path: '/dashboard-operacional', icon: '📊' },
        { label: 'Dashboard Parcelas', path: '/dashboard-parcelas', icon: '🧾' },
        {
          label: 'Cadastros',
          icon: '🧾',
          submenu: [
            { label: 'Alunos', path: '/alunos', icon: '👨‍🎓' },
            { label: 'Responsáveis', path: '/responsaveis/pendentes', icon: '👤' },
          ]
        },
        {
          label: 'Acadêmico',
          icon: '🎓',
          submenu: [
            { label: 'Turmas', path: '/turmas', icon: '📚' },
            { label: 'Matrículas', path: '/matriculas', icon: '📝' },
            { label: 'Relatórios Operacionais', path: '/relatorios-operacionais', icon: '📊' },
          ]
        },
        { label: 'Relatório de Aulas', path: '/relatorio-mensal', icon: '📊' },
        {
          label: 'Financeiro',
          icon: '💵',
          submenu: [
            { label: 'Fluxo de Caixa', path: '/fluxo-caixa', icon: '📈' },
            { label: 'Receitas', path: '/receitas', icon: '💰' },
            { label: 'Parcelas', path: '/parcelas', icon: '🧾' },
            { label: 'Faturas - Operacional', path: '/faturas/operacional', icon: '⚙️' },
            { label: 'Faturas - Gestão', path: '/faturas/gestao', icon: '🗂️' },
            { label: 'Despesas', path: '/despesas', icon: '💸' },
            { label: 'Pagamentos', path: '/folha-pagamento', icon: '💵' },
            { label: 'Fornecedores', path: '/fornecedores', icon: '🏪' },
            { label: 'Categorias Despesa', path: '/categorias-despesa', icon: '🏷️' },
          ]
        },
      ],
      PROFESSOR: [
        { label: 'Início', path: '/professor', icon: '🏠' },
        { label: 'Minhas Aulas', path: '/aulas-dadas', icon: '✅' },
        { label: 'Relatório de Aulas', path: '/relatorio-mensal', icon: '📊' },
        { label: 'Turmas', path: '/turmas', icon: '📚' },
        { label: 'Pagamentos', path: '/folha-pagamento', icon: '💵' },
      ],
      ALUNO: [
        { label: 'Meus Dados', path: '/meus-dados', icon: '👤' },
        { label: 'Minhas Matrículas', path: '/minhas-matriculas', icon: '📝' },
        { label: 'Turmas Abertas', path: '/turmas-abertas', icon: '📚' },
      ],
      RESPONSAVEL: [
        { label: 'Portal do Responsável', path: '/responsavel/dashboard', icon: '🏠' },
        { label: 'Alunos', path: '/alunos', icon: '👨‍🎓' },
        { label: 'Matrículas', path: '/matriculas', icon: '📝' },
        { label: 'Turmas', path: '/turmas', icon: '📚' },
      ],
      FINANCEIRO: [
        { label: 'Dashboard Financeiro', path: '/dashboard-financeiro', icon: '💰' },
        {
          label: 'Financeiro',
          icon: '💵',
          submenu: [
            { label: 'Fluxo de Caixa', path: '/fluxo-caixa', icon: '📈' },
            { label: 'Receitas', path: '/receitas', icon: '💰' },
            { label: 'Parcelas', path: '/parcelas', icon: '🧾' },
            { label: 'Faturas - Operacional', path: '/faturas/operacional', icon: '⚙️' },
            { label: 'Faturas - Gestão', path: '/faturas/gestao', icon: '🗂️' },
            { label: 'Despesas', path: '/despesas', icon: '💸' },
            { label: 'Fornecedores', path: '/fornecedores', icon: '🏪' },
            { label: 'Categorias Despesa', path: '/categorias-despesa', icon: '🏷️' },
            { label: 'Relatórios Contábeis', path: '/relatorios-contabeis', icon: '📑' },
          ]
        },
      ],
    };

    const rawItems = isPlatformAdmin ? [] : (itemsByRole[roleKey] || baseItems);
    // PROFESSOR ja recebe sua propria entrada "Inicio" (apontando para /professor)
    // dentro de itemsByRole.PROFESSOR — nao duplicar com o "Inicio" generico (/inicio).
    // "Presenças" vive dentro do grupo "Academico" -- role-neutro aqui (para
    // perfis sem grupo "Academico" proprio) ou dentro do array de itemsByRole
    // (ADMIN/RH/OFFICE, ver rolesComAcademicoProprio acima). Em ambos os
    // casos sua visibilidade depende exclusivamente de canAccess/
    // podeVisualizar('presencas_gestao'), igual para qualquer perfil -- nao
    // ha barreira fixa por nome de perfil. Perfis sem grant sao filtrados por
    // canAccess mais abaixo; nenhum papel recebe o item automaticamente.
    const commonItems = isPlatformAdmin || roleKey === 'PROFESSOR'
      ? []
      : [
          { label: 'Início', path: '/inicio', icon: '🏠' },
          ...(rolesComAcademicoProprio.has(roleKey)
            ? []
            : [{ label: 'Acadêmico', icon: '🎓', submenu: [presencasSubmenuItem] }]),
        ];
    const platformAdminItems = isPlatformAdmin
      ? [
          {
            label: 'Dashboard',
            icon: '🧩',
            submenu: [
              { label: 'Painel SaaS', path: '/platform', icon: '🧩' },
            ],
          },
          {
            label: 'Clientes',
            icon: '📨',
            submenu: [
              { label: 'Assinaturas SaaS', path: '/platform/saas/subscriptions-dashboard', icon: '📈' },
              { label: 'Solicitacoes Assinatura', path: '/platform/saas/subscription-change-requests', icon: '📨' },
              { label: 'Timeline SaaS', path: '/platform/saas/timeline', icon: '🧾' },
            ],
          },
          {
            label: 'Operacao',
            icon: '🧭',
            submenu: [
              { label: 'Runtime Incidents', path: '/platform/saas-billing/runtime/incidents', icon: '🚦' },
              { label: 'Billing Operations', path: '/platform/saas-billing/operations', icon: '🧰' },
              { label: 'Recovery Console', path: '/platform/saas-billing/runtime/recovery-operations', icon: '🛟' },
              { label: 'Runtime Cockpit', path: '/platform/saas-billing/runtime/cockpit', icon: '🧭' },
            ],
          },
          {
            label: 'Monitoramento',
            icon: '🚨',
            submenu: [
              { label: 'Alert Center', path: '/platform/saas/alert-center', icon: '🚨' },
              { label: 'Operational Health', path: '/platform/saas/operational-health', icon: '🩺' },
              { label: 'Operational Events', path: '/platform/saas/operational-events', icon: '📍' },
              { label: 'Go-live Readiness', path: '/platform/saas/go-live-readiness', icon: '✅' },
            ],
          },
          {
            label: 'Administracao',
            icon: '🔐',
            submenu: [
              { label: 'IAM / Permissoes', path: '/platform/iam', icon: '🔐' },
              { label: 'Feature Flags', path: '/platform/saas/feature-flags', icon: '🚦' },
              { label: 'Provider Config', path: '/platform/saas-billing/runtime/provider-config', icon: '🔧' },
              { label: 'Catalogo SaaS', path: '/platform/saas-billing/catalog', icon: '📦' },
            ],
          },
        ]
      : [];

    const filteredPlatformAdminItems = isPlatformAdmin
      ? filterPlatformMenuItemsByRouteAccess(platformAdminItems, platformPermissionState)
      : [];

    const canAccess = (path) => {
      if (isPlatformAdmin) {
        return path.startsWith('/platform');
      }

      if (isAdmin) {
        return hasOperationalTenant;
      }

      if (!hasOperationalTenant) return false;

      const recurso = routeResourceMap[path];

      // Rotas sem mapeamento explícito são públicas no menu
      if (!recurso) return true;

      if (permissaoLoading || permissaoError || !permissoes) return false;

      return podeVisualizar(recurso);
    };

    const filterItems = (items) =>
      items
        .map((item) => {
          if (item.submenu) {
            const submenu = filterItems(item.submenu);
            if (!submenu.length) return null;
            return { ...item, submenu };
          }
          if (item.path) {
            const allowed = canAccess(item.path) && perfilTemMenuItem(item.path);
            if (!allowed) return null;
          }
          return item;
        })
        .filter(Boolean);

    return filterItems([...commonItems, ...rawItems, ...filteredPlatformAdminItems]);
  };

  const menuItems = getMenuItems();

  const isItemActive = (itemPath) => {
    const normalizedPath = String(itemPath || '').trim();
    if (!normalizedPath) {
      return false;
    }

    if (normalizedPath === '/platform') {
      return currentPath === '/platform';
    }

    return currentPath === normalizedPath || currentPath.startsWith(`${normalizedPath}/`);
  };

  const roleBadgeClasses = [
    'role-badge',
    `role-${roleBadgeClass}`,
    isPlatformSidebarTheme && isPlatformAdmin ? 'role-admin-platform' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleAlterarEmail = () => {
    setIsEmailModalVisible(true);
    emailForm.setFieldsValue({ novo_email: resolvedUserEmail });
  };

  const handleAlterarSenha = () => {
    setIsSenhaModalVisible(true);
  };

  const handleSubmitEmail = async (values) => {
    try {
      await usuarioService.alterarMeuEmail(values);
      message.success('Email alterado com sucesso! Por favor, faça login novamente.');
      emailForm.resetFields();
      setIsEmailModalVisible(false);
      setTimeout(() => logout(), 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao alterar email';
      message.error(errorMsg);
    }
  };

  const handleSubmitSenha = async (values) => {
    try {
      await usuarioService.alterarMinhaSenha(values);
      message.success('Senha alterada com sucesso! Por favor, faça login novamente.');
      senhaForm.resetFields();
      setIsSenhaModalVisible(false);
      setTimeout(() => logout(), 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao alterar senha';
      message.error(errorMsg);
    }
  };

  const profileMenu = (
    <Menu>
      <Menu.Item key="email" icon={<UserOutlined />} onClick={handleAlterarEmail}>
        Alterar Email
      </Menu.Item>
      <Menu.Item key="senha" icon={<KeyOutlined />} onClick={handleAlterarSenha}>
        Alterar Senha
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout} danger>
        Sair
      </Menu.Item>
    </Menu>
  );

  const renderNavList = (onNavigate) => (
    <ul className="nav-list">
      {menuItems.map((item) => (
        <li key={item.label} className="nav-item">
          {item.submenu ? (
            <>
              <div
                className={`nav-link nav-link-submenu${item.submenu.some((subitem) => isItemActive(subitem.path)) ? ' nav-link-active' : ''}`}
                onClick={() => toggleSubmenu(item.label)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                <span className="submenu-arrow">{openSubmenu === item.label ? '▼' : '▶'}</span>
              </div>
              {openSubmenu === item.label && (
                <ul className="submenu">
                  {item.submenu.map((subitem) => (
                    <li key={subitem.path} className="submenu-item">
                      <Link
                        to={subitem.path}
                        className={`submenu-link${isItemActive(subitem.path) ? ' nav-link-active' : ''}`}
                        onClick={onNavigate}
                      >
                        <span className="nav-icon">{subitem.icon}</span>
                        <span className="nav-label">{subitem.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <Link
              to={item.path}
              className={`nav-link${isItemActive(item.path) ? ' nav-link-active' : ''}`}
              onClick={onNavigate}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  const renderFooterContent = () => (
    <Dropdown overlay={profileMenu} trigger={['click']} placement="topLeft">
      <div className="user-info" style={{ cursor: 'pointer' }}>
        <div className="user-avatar">
          {avatarInitial}
        </div>
        <div className="user-details">
          <p className="user-name">{resolvedUserName}</p>
          <p className="user-email">{resolvedUserEmail || '-'}</p>
        </div>
        <DownOutlined style={{ marginLeft: 'auto', fontSize: '12px' }} />
      </div>
    </Dropdown>
  );

  const closeMobileDrawer = () => setIsMobileDrawerOpen(false);

  return (
    <aside className={`sidebar${isPlatformSidebarTheme ? ' sidebar-platform-theme' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <h2 className="app-title">FisiSaaS</h2>
          <div className="user-badge">
            <span className={roleBadgeClasses}>{resolvedRole}</span>
          </div>
        </div>
        <button
          type="button"
          className="sidebar-menu-toggle"
          aria-label="Abrir menu"
          onClick={() => setIsMobileDrawerOpen(true)}
        >
          <MenuOutlined aria-hidden="true" />
          <span className="sidebar-menu-toggle-label">Menu</span>
        </button>
      </div>

      <nav className="sidebar-nav">
        {renderNavList()}
      </nav>

      <div className="sidebar-footer">
        {renderFooterContent()}
      </div>

      <Drawer
        title="Menu"
        placement="left"
        width={280}
        zIndex={1100}
        open={isMobileDrawerOpen}
        onClose={closeMobileDrawer}
        styles={{ body: { padding: 0 } }}
        className="sidebar-mobile-drawer"
      >
        <div className={`sidebar-drawer-content${isPlatformSidebarTheme ? ' sidebar-platform-theme' : ''}`}>
          <div className="sidebar-drawer-nav">
            {renderNavList(closeMobileDrawer)}
          </div>
          <div className="sidebar-drawer-footer">
            {renderFooterContent()}
          </div>
        </div>
      </Drawer>

      {/* Modal Alterar Email */}
      <Modal
        title="Alterar Email"
        open={isEmailModalVisible}
        onCancel={() => {
          setIsEmailModalVisible(false);
          emailForm.resetFields();
        }}
        footer={null}
      >
        <Form form={emailForm} onFinish={handleSubmitEmail} layout="vertical">
          <Form.Item
            label="Novo Email"
            name="novo_email"
            rules={[
              { required: true, message: 'Digite o novo email' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input placeholder="novo@email.com" />
          </Form.Item>
          <Form.Item
            label="Senha Atual"
            name="senha_atual"
            rules={[{ required: true, message: 'Digite sua senha atual para confirmar' }]}
          >
            <Input.Password placeholder="Digite sua senha atual" />
          </Form.Item>
          <Form.Item>
            <button type="submit" className="logout-btn" style={{ width: '100%' }}>
              Alterar Email
            </button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Alterar Senha */}
      <Modal
        title="Alterar Senha"
        open={isSenhaModalVisible}
        onCancel={() => {
          setIsSenhaModalVisible(false);
          senhaForm.resetFields();
        }}
        footer={null}
      >
        <Form form={senhaForm} onFinish={handleSubmitSenha} layout="vertical">
          <Form.Item
            label="Senha Atual"
            name="senha_atual"
            rules={[{ required: true, message: 'Digite sua senha atual' }]}
          >
            <Input.Password placeholder="Senha atual" />
          </Form.Item>
          <Form.Item
            label="Nova Senha"
            name="nova_senha"
            rules={[
              { required: true, message: 'Digite a nova senha' },
              { min: MIN_PASSWORD_LENGTH, message: PASSWORD_MIN_LENGTH_MESSAGE }
            ]}
          >
            <Input.Password placeholder={PASSWORD_PLACEHOLDER} />
          </Form.Item>
          <Form.Item
            label="Confirmar Nova Senha"
            name="confirmar_nova_senha"
            dependencies={['nova_senha']}
            rules={[
              { required: true, message: 'Confirme a nova senha' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('nova_senha') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não coincidem'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirme a nova senha" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Alterar Senha
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </aside>
  );
};

export default Sidebar;
